import type { Position } from "geojson";
import { z } from "zod";

import type {
  ItinerarySegment,
  ItineraryWaypoint,
  TrailDifficultyType,
} from "@mountayaapp/types/api/api";

import { extractLineStrings, extractPoint } from "./geometry";

/**
 * elevationPointSchema is one sample on the profile chart: cumulative distance
 * (km), elevation (m), the [lng, lat] of the coordinate it was read from, and the
 * owning segment. Zod-first because the video profile source persists an array of
 * these in the UI store (it crosses the route-set tool boundary), so the shape is
 * validated rather than trusted; the chart's in-process builders read the derived
 * ElevationPoint type. The coordinate rides along so the synced chart cursor can
 * place a map marker at the hovered point and the tooltip can show its position
 * without a second walk of the geometry.
 */
export const elevationPointSchema = z.object({
  distanceKm: z.number(),
  elevation: z.number(),
  lat: z.number(),
  lng: z.number(),
  segmentId: z.string(),
});

/**
 * ElevationPoint is one sample on the profile chart, derived from
 * elevationPointSchema.
 */
export type ElevationPoint = z.infer<typeof elevationPointSchema>;

/**
 * ProfileCharacteristics is the summary the profile renders above the chart:
 * total distance (m), the estimated duration (s), the altitude min/max (m), total
 * ascent + descent (m), the slope range (steepest descending + ascending
 * gradient, degrees), and the trail-difficulty range (easiest + hardest graded
 * stretch). Every field is nullable because the source metrics are null for a 2D
 * segment or one whose metrics were never computed — a null field renders as
 * absent rather than zero. It is scoped to the selected segment, or aggregated
 * across the whole itinerary.
 */
export interface ProfileCharacteristics {
  altitudeMax: number | null;
  altitudeMin: number | null;
  ascent: number | null;
  descent: number | null;
  distance: number | null;
  duration: number | null;
  slopeMax: number | null;
  slopeMin: number | null;
  trailDifficultyMax: TrailDifficultyType | null;
  trailDifficultyMin: TrailDifficultyType | null;
}

/**
 * TRAIL_DIFFICULTY_RANK orders each trail-difficulty grade within its own scale,
 * so a range can span the easiest and hardest stretch of a route. `unknown` maps
 * to null: it means the way carries no difficulty tag, not that the ground is
 * easy, so it takes no position on the scale and is skipped when folding a range.
 *
 * The SAC and mountain-biking grades share one table because they share one
 * field, and comparing across them would be meaningless — the ranks are only
 * ever compared within one route, which is graded on a single scale (the
 * activity's routing profile picks it, and an activity is itinerary-wide).
 * Keyed on the generated TrailDifficultyType so a new grade fails to typecheck
 * until it is ranked.
 */
export const TRAIL_DIFFICULTY_RANK: Record<TrailDifficultyType, number | null> = {
  "alpine-hiking": 4,
  "demanding-alpine-hiking": 5,
  "demanding-mountain-hiking": 3,
  "difficult-alpine-hiking": 6,
  hiking: 1,
  "mountain-hiking": 2,
  "mtb-0": 0,
  "mtb-1": 1,
  "mtb-2": 2,
  "mtb-3": 3,
  "mtb-4": 4,
  "mtb-5": 5,
  "mtb-6": 6,
  unknown: null,
};

/**
 * ProfileSeries describes one segment's contribution to the area chart: the
 * segment id (the data key its elevations live under) and the segment color the
 * band is filled with. The profile draws one area series per segment so each
 * segment's stretch of the curve fills with its own color; the series order is the
 * segment chain order.
 */
export interface ProfileSeries {
  color: string | null;
  segmentId: string;
}

/**
 * ProfileRow is one row of the pivoted area-chart data: a cumulative distance plus
 * one elevation column per segment, keyed by segment id. A sample contributes its
 * elevation only to its own segment's column, leaving the other columns undefined —
 * so each per-segment area series paints only across its own distance stretch and
 * the chart shows a colored band per segment. The boundary coordinate shared by two
 * adjacent segments is emitted once per segment (buildElevationPoints walks each
 * segment's line), so the bands meet with no horizontal gap.
 */
export interface ProfileRow {
  distanceKm: number;
  [segmentId: string]: number | undefined;
}

/**
 * WaypointProjection places a waypoint on the distance axis: the waypoint id and
 * the cumulative distance (km) along the itinerary line where it sits. A single
 * waypoint can yield several projections — a looping or doubling-back route passes
 * near the same point more than once — so the profile can mark each close pass.
 */
export interface WaypointProjection {
  distanceKm: number;
  waypointId: string;
}

/**
 * IndexedInfo is one coordinate-index-ranged analysis entry stored on a segment:
 * the inclusive [from_index, to_index] range into that segment's route coordinates
 * and the value classified over it. It is the common shape of slope_info,
 * surface_info, and waytype_info, so buildInfoBands can map any of them onto the
 * distance axis with one walk. Exported so other studio surfaces (the live route
 * preview that projects a routed response onto a segment) can shape their entries
 * to the same contract the panel reads.
 */
export interface IndexedInfo<T> {
  from_index: number;
  to_index: number;
  value: T;
}

/**
 * InfoBand is one analysis stretch placed on the profile's distance axis: the
 * cumulative start/end distance (km) and the value (a surface/way-type enum or a
 * slope angle) classified over it. The surface/way-type/slope bars below the chart
 * render these as colored cells; the color resolution is the caller's, so the band
 * stays a pure geometry→value mapping.
 */
export interface InfoBand<T> {
  endKm: number;
  startKm: number;
  value: T;
}

/**
 * bandValueAt returns the value of the band whose [startKm, endKm] stretch
 * contains the given cumulative distance (km), or null when no band covers it (a
 * gap between bands, or a distance past the last band's end). The bands are
 * ascending and non-overlapping the way buildInfoBands emits them, so a forward
 * scan stops at the first match; the start is inclusive and the end exclusive,
 * except the final band includes its own end so the very last sample still
 * resolves. It is the per-distance reader the video profile overlay calls once per
 * frame to read the surface / way-type / slope under the moving flight position,
 * the band counterpart to activePointAt's nearest-sample lookup for elevation.
 */
export const bandValueAt = <T>(bands: InfoBand<T>[], distanceKm: number): T | null => {
  for (let index = 0; index < bands.length; index += 1) {
    const band = bands[index];
    if (!band) {
      continue;
    }

    // Start inclusive, end exclusive so adjacent bands hand off cleanly; the last
    // band also claims its own end so a sample exactly at the route end resolves.
    const isLast = index === bands.length - 1;
    if (
      distanceKm >= band.startKm &&
      (distanceKm < band.endKm || (isLast && distanceKm <= band.endKm))
    ) {
      return band.value;
    }
  }

  return null;
};

/**
 * buildInfoBands maps a segment's coordinate-index-ranged analysis (slope,
 * surface, or way type) onto the profile's cumulative distance axis. The stored
 * from_index/to_index reference each segment's own route-coordinate array, so it
 * walks every segment's leaf LineString accumulating distance, resolves each
 * entry's index range to a start/end distance, and offsets by the itinerary
 * distance preceding the segment — so the bands line up with the chart whether the
 * profile is scoped to one segment or the whole itinerary. `select` pulls the info
 * array off a segment (s => s.surface_info, etc.); a segment with no line or no
 * info contributes no bands. An index beyond the segment's coordinates clamps to
 * its last vertex so a stale range never reads past the line.
 *
 * `select` is written as the bare field read, and those fields are nullable on the
 * wire — the API layer normalizes an absent analysis to an empty array on the way in,
 * so a null never actually reaches here, but the wire type still spells one. Accepting
 * it keeps that residue in this one signature instead of making all nineteen call
 * sites re-state it.
 */
export const buildInfoBands = <T>(
  segments: ItinerarySegment[],
  select: (segment: ItinerarySegment) => IndexedInfo<T>[] | null | undefined,
): InfoBand<T>[] => {
  const bands: InfoBand<T>[] = [];
  let itineraryKm = 0;

  for (const segment of segments) {
    // Concatenate the segment's leaf LineStrings into one coordinate array — the
    // same array the stored indices reference — and precompute the cumulative
    // distance at each vertex so an index resolves to a distance in O(1).
    const coordinates: Position[] = [];
    for (const line of extractLineStrings(segment.geojson)) {
      coordinates.push(...line.coordinates);
    }

    if (coordinates.length >= 2) {
      const cumulativeKm: number[] = [0];
      for (let index = 1; index < coordinates.length; index += 1) {
        const previous = coordinates[index - 1];
        const current = coordinates[index];
        const previousKm = cumulativeKm[index - 1] ?? 0;
        cumulativeKm[index] =
          previous && current ? previousKm + haversineKm(previous, current) : previousKm;
      }

      const lastIndex = coordinates.length - 1;
      const distanceAt = (index: number): number =>
        cumulativeKm[Math.min(Math.max(index, 0), lastIndex)] ?? 0;

      for (const info of select(segment) ?? []) {
        const startKm = itineraryKm + distanceAt(info.from_index);
        const endKm = itineraryKm + distanceAt(info.to_index);

        // Drop bands whose endpoints collapse to the same vertex (a stored entry
        // referencing a single index, or two indices on a duplicate coordinate) —
        // they cannot paint a strip and would otherwise key-collide with their
        // neighbour. Merge a band with the previous one when it carries the same
        // value and butts up against it, so the analysis bar shows one continuous
        // stretch instead of two visually identical cells.
        if (endKm - startKm <= 0) {
          continue;
        }

        const previous = bands.at(-1);
        if (previous && previous.value === info.value && previous.endKm >= startKm) {
          previous.endKm = endKm;

          continue;
        }

        bands.push({ endKm, startKm, value: info.value });
      }

      itineraryKm += cumulativeKm[lastIndex] ?? 0;
    }
  }

  return bands;
};

const EARTH_RADIUS_KM = 6371;

/**
 * haversineKm is the great-circle distance between two [lng, lat] positions in
 * kilometres.
 */
export const haversineKm = (a: Position, b: Position): number => {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const [lngA = 0, latA = 0] = a;
  const [lngB = 0, latB = 0] = b;
  const dLat = toRad(latB - latA);
  const dLng = toRad(lngB - lngA);
  const sinLat = Math.sin(dLat / 2);
  const sinLng = Math.sin(dLng / 2);
  const h = sinLat * sinLat + Math.cos(toRad(latA)) * Math.cos(toRad(latB)) * sinLng * sinLng;

  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * buildElevationPoints walks the LineString extracted from each segment's
 * geometry, accumulating distance and reading the z (3rd) coordinate as
 * elevation. Segment geometry is a GeometryCollection, so the LineString is
 * pulled from it (a directions segment's via MultiPoint is ignored); a segment
 * with no LineString or only 2D coordinates contributes no points.
 */
export const buildElevationPoints = (segments: ItinerarySegment[]): ElevationPoint[] => {
  const points: ElevationPoint[] = [];
  let distanceKm = 0;

  for (const segment of segments) {
    for (const line of extractLineStrings(segment.geojson)) {
      const coordinates = line.coordinates;
      for (let index = 0; index < coordinates.length; index += 1) {
        const position = coordinates[index];
        if (!position) {
          continue;
        }

        if (index > 0) {
          const previous = coordinates[index - 1];
          if (previous) {
            distanceKm += haversineKm(previous, position);
          }
        }

        const elevation = position[2];
        if (elevation === undefined) {
          continue;
        }

        const [lng = 0, lat = 0] = position;
        points.push({ distanceKm, elevation, lat, lng, segmentId: segment.id });
      }
    }
  }

  return points;
};

/**
 * downsampleElevation thins an elevation series down to at most maxPoints using a
 * uniform stride, always keeping the first and last sample so the profile spans
 * the full distance. The series is returned unchanged when it already fits within
 * maxPoints (or maxPoints is below 2, which cannot describe a line). The chart
 * reads thousands of coordinates straight from the geometry, so capping the
 * sample count keeps the render cheap without distorting the curve's shape.
 */
export const downsampleElevation = (
  points: ElevationPoint[],
  maxPoints: number,
): ElevationPoint[] => {
  if (maxPoints < 2 || points.length <= maxPoints) {
    return points;
  }

  // Stride across the source so the kept indices spread evenly; the last point is
  // appended explicitly so the profile always reaches the true end distance.
  const last = points.length - 1;
  const stride = last / (maxPoints - 1);
  const sampled: ElevationPoint[] = [];
  for (let slot = 0; slot < maxPoints - 1; slot += 1) {
    const point = points[Math.round(slot * stride)];
    if (point) {
      sampled.push(point);
    }
  }

  const lastPoint = points[last];
  if (lastPoint) {
    sampled.push(lastPoint);
  }

  return sampled;
};

/**
 * minOrNull returns the smaller of two nullable numbers, treating null as absent:
 * two nulls stay null, one null yields the other. Used to fold an altitude minimum
 * across segments where any segment's metric may be missing.
 */
export const minOrNull = (a: number | null, b: number | null): number | null => {
  if (a === null) {
    return b;
  }

  if (b === null) {
    return a;
  }

  return Math.min(a, b);
};

/** maxOrNull is the maximum counterpart of minOrNull, folding an altitude/slope maximum. */
export const maxOrNull = (a: number | null, b: number | null): number | null => {
  if (a === null) {
    return b;
  }

  if (b === null) {
    return a;
  }

  return Math.max(a, b);
};

/**
 * sumOrNull adds two nullable numbers, treating null as absent: two nulls stay
 * null, one null yields the other, two numbers add. Used to fold a total distance
 * or ascent across segments so the aggregate stays null only when no segment
 * carried the metric at all.
 */
export const sumOrNull = (a: number | null, b: number | null): number | null => {
  if (a === null) {
    return b;
  }

  if (b === null) {
    return a;
  }

  return a + b;
};

/**
 * profileCharacteristics folds the given segments' stored metrics into the summary
 * the profile renders above the chart: distance, duration, and ascent/descent are
 * summed, altitude min/max are the extremes, the slope range spans the steepest
 * descending gradient (gradient_min, negative) to the steepest ascending one
 * (gradient_max, positive), and the trail-difficulty range spans the easiest and
 * hardest graded stretch. The metrics are read straight off the segments — the
 * server computes and stores them — so a single-segment selection reads that
 * segment's own metrics and the whole itinerary reads their aggregate. Each field
 * stays null until a segment contributes it, so a 2D itinerary with no elevation
 * yields an all-null summary the caller renders as absent rather than zero; the
 * duration is the sum of the non-null segment durations, or null when none carry one.
 *
 * Trail difficulty skips `unknown` stretches: an untagged way makes no difficulty
 * claim, so counting it as the bottom of the range would read as "this route has
 * easy sections" on a route that is simply unclassified. A route whose stretches
 * are all unknown therefore yields a null range and the caller renders no value.
 */
export const profileCharacteristics = (segments: ItinerarySegment[]): ProfileCharacteristics => {
  const summary: ProfileCharacteristics = {
    altitudeMax: null,
    altitudeMin: null,
    ascent: null,
    descent: null,
    distance: null,
    duration: null,
    slopeMax: null,
    slopeMin: null,
    trailDifficultyMax: null,
    trailDifficultyMin: null,
  };

  // The ranks of the range bounds, tracked alongside the values so each
  // comparison is one lookup rather than a re-resolve of the current extremes.
  let hardestRank: number | null = null;
  let easiestRank: number | null = null;

  for (const segment of segments) {
    summary.distance = sumOrNull(summary.distance, segment.distance);
    summary.duration = sumOrNull(summary.duration, segment.duration);
    summary.ascent = sumOrNull(summary.ascent, segment.ascent);
    summary.descent = sumOrNull(summary.descent, segment.descent);
    summary.altitudeMin = minOrNull(summary.altitudeMin, segment.altitude_min);
    summary.altitudeMax = maxOrNull(summary.altitudeMax, segment.altitude_max);
    summary.slopeMin = minOrNull(summary.slopeMin, segment.gradient_min ?? null);
    summary.slopeMax = maxOrNull(summary.slopeMax, segment.gradient_max ?? null);

    for (const entry of segment.trail_difficulty_info ?? []) {
      const rank = TRAIL_DIFFICULTY_RANK[entry.value];
      if (rank === null) {
        continue;
      }

      if (hardestRank === null || rank > hardestRank) {
        hardestRank = rank;
        summary.trailDifficultyMax = entry.value;
      }

      if (easiestRank === null || rank < easiestRank) {
        easiestRank = rank;
        summary.trailDifficultyMin = entry.value;
      }
    }
  }

  return summary;
};

/**
 * profileSeries lists one entry per segment in chain order — the id the chart keys
 * a segment's elevation column on and the color its band is filled with — so the
 * area chart can draw one colored series per segment. A segment with no explicit
 * color carries null; the caller resolves the fallback (the default activity
 * color), keeping the single color source the map render shares.
 */
export const profileSeries = (segments: ItinerarySegment[]): ProfileSeries[] =>
  segments.map((segment) => ({ color: segment.color, segmentId: segment.id }));

/**
 * pivotElevationBySegment turns a flat elevation series into rows the area chart
 * plots: each row carries the cumulative distance and the sample's elevation under
 * its own segment id, leaving every other segment column undefined. One area series
 * per segment then paints only across that segment's own distance stretch, so the
 * chart shows a colored band per segment rather than one uniform fill. Points are
 * already in distance order (buildElevationPoints walks the chain), so the rows are
 * emitted in input order.
 */
export const pivotElevationBySegment = (points: ElevationPoint[]): ProfileRow[] =>
  points.map((point) => ({ [point.segmentId]: point.elevation, distanceKm: point.distanceKm }));

/**
 * nearestPointIndexAt returns the index of the sample whose cumulative distance (km)
 * is closest to the given distance, or -1 for an empty series. It binary-searches the
 * points — which are ascending by distanceKm (buildElevationPoints walks the chain
 * head-to-tail accumulating distance) — to the insertion point, then compares the two
 * straddling samples and returns the one with the smaller absolute gap (the lower
 * index on a tie). The video profile overlay reads it once per frame to place the
 * moving flight position on the curve, so the lookup is O(log P) rather than a full
 * scan of every sample each frame.
 */
export const nearestPointIndexAt = (points: ElevationPoint[], distanceKm: number): number => {
  if (points.length === 0) {
    return -1;
  }

  // Binary-search for the first index whose distance is >= the target; `low` lands on
  // the sample at or just past the target, `low - 1` on the one just before it.
  let low = 0;
  let high = points.length - 1;
  while (low < high) {
    const mid = (low + high) >> 1;
    if ((points[mid]?.distanceKm ?? 0) < distanceKm) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }

  // Compare the straddling samples (the one at `low` and its predecessor) and keep the
  // closer; ties favor the lower index so the result is deterministic. The clamp keeps
  // a target before the first or past the last sample pinned to the nearest endpoint.
  const after = low;
  const before = low > 0 ? low - 1 : 0;
  const afterGap = Math.abs((points[after]?.distanceKm ?? 0) - distanceKm);
  const beforeGap = Math.abs((points[before]?.distanceKm ?? 0) - distanceKm);

  return beforeGap <= afterGap ? before : after;
};

/**
 * activePointAt returns the sample nearest the given cumulative distance (km),
 * used to drive the profile's synced vertical line + tooltip from a hover position
 * and the video overlay's per-frame elevation readout. It resolves the nearest index
 * via nearestPointIndexAt (an O(log P) binary search over the ascending-by-distance
 * series), returning null for an empty series so the caller renders no marker.
 */
export const activePointAt = (
  points: ElevationPoint[],
  distanceKm: number,
): ElevationPoint | null => {
  const index = nearestPointIndexAt(points, distanceKm);

  return index < 0 ? null : (points[index] ?? null);
};

/** EARTH_RADIUS_M is the mean Earth radius in metres, for the planar leg projection scale. */
const EARTH_RADIUS_M = EARTH_RADIUS_KM * 1000;

/** DEGREE_TO_RADIANS converts a coordinate degree to radians for the local metric scale. */
const DEGREE_TO_RADIANS = Math.PI / 180;

/**
 * WAYPOINT_PROXIMITY_METERS is how close the itinerary line must pass to a waypoint
 * for the waypoint to be marked on the profile at that distance. A waypoint dropped
 * beside (not exactly on) the route still projects onto it within this radius; one
 * farther than this is an off-route point with no place on the profile. The value
 * is generous enough to catch a POI snapped near the line yet tight enough that an
 * unrelated waypoint elsewhere on the map does not appear.
 */
export const WAYPOINT_PROXIMITY_METERS = 60;

/**
 * LegApproach is the closest approach of one line leg to a waypoint: the
 * perpendicular distance in metres and the cumulative distance (km) along the line
 * of the foot of that perpendicular. projectWaypoints scans these to find each
 * close pass.
 */
interface LegApproach {
  distanceKm: number;
  meters: number;
}

/**
 * legApproach computes a waypoint's closest approach to one line leg: it projects
 * the waypoint onto the leg in a local east/north metric frame (longitude scaled by
 * cos(latitude) so a degree east matches a degree north in metres), clamps the
 * projection to the leg, and returns the perpendicular distance plus the cumulative
 * distance of the foot. The local planar frame is accurate over an editor-resolution
 * leg, the same approximation pointAtFraction's interpolation relies on.
 */
const legApproach = (
  previous: Position,
  current: Position,
  waypoint: Position,
  legStartKm: number,
  legMeters: number,
): LegApproach => {
  const [aLng = 0, aLat = 0] = previous;
  const [bLng = 0, bLat = 0] = current;
  const [wLng = 0, wLat = 0] = waypoint;

  // Scale longitude by cos(latitude) at the leg's start so east/north distances are
  // comparable; convert the degree deltas to metres via the Earth radius.
  const lngScale = Math.cos(aLat * DEGREE_TO_RADIANS);
  const toMeters = EARTH_RADIUS_M * DEGREE_TO_RADIANS;
  const ax = aLng * lngScale * toMeters;
  const ay = aLat * toMeters;
  const bx = bLng * lngScale * toMeters;
  const by = bLat * toMeters;
  const wx = wLng * lngScale * toMeters;
  const wy = wLat * toMeters;

  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  // A zero-length leg collapses to its start; the foot is the start point.
  const t =
    lengthSq === 0 ? 0 : Math.min(1, Math.max(0, ((wx - ax) * dx + (wy - ay) * dy) / lengthSq));

  const footX = ax + dx * t;
  const footY = ay + dy * t;
  const meters = Math.hypot(wx - footX, wy - footY);

  return { distanceKm: legStartKm + (legMeters / 1000) * t, meters };
};

/**
 * projectWaypoints places each waypoint on the profile's distance axis at every
 * point the itinerary line passes within WAYPOINT_PROXIMITY_METERS of it. The line
 * is the segments' concatenated leaf LineStrings in chain order (the same walk
 * buildElevationPoints does), so a projection's distance lines up with the chart.
 * A waypoint can yield several projections: each contiguous run of below-threshold
 * legs is one close pass, collapsed to its single nearest point, so a looping or
 * doubling-back route marks the waypoint once per pass rather than once per leg. A
 * waypoint the line never approaches yields nothing. Display only — the projection
 * carries no authoring affordance.
 */
export const projectWaypoints = (
  segments: ItinerarySegment[],
  waypoints: ItineraryWaypoint[],
  proximityMeters = WAYPOINT_PROXIMITY_METERS,
): WaypointProjection[] => {
  // Concatenate the chain's line coordinates once, carrying the cumulative distance
  // at each vertex so a leg's foot distance maps straight onto the chart axis.
  const coordinates: Position[] = [];
  for (const segment of segments) {
    for (const line of extractLineStrings(segment.geojson)) {
      coordinates.push(...line.coordinates);
    }
  }

  if (coordinates.length < 2) {
    return [];
  }

  const cumulativeKm: number[] = [0];
  for (let index = 1; index < coordinates.length; index += 1) {
    const previous = coordinates[index - 1];
    const current = coordinates[index];
    const previousKm = cumulativeKm[index - 1] ?? 0;
    cumulativeKm[index] =
      previous && current ? previousKm + haversineKm(previous, current) : previousKm;
  }

  const projections: WaypointProjection[] = [];
  for (const waypoint of waypoints) {
    const point = extractPoint(waypoint.geojson);
    if (!point) {
      continue;
    }

    const coordinate = point.coordinates;
    // Track the current close pass: the nearest leg approach seen since the line
    // last came within range. A leg out of range closes the open pass.
    let best: LegApproach | null = null;
    for (let index = 1; index < coordinates.length; index += 1) {
      const previous = coordinates[index - 1];
      const current = coordinates[index];
      const legStartKm = cumulativeKm[index - 1] ?? 0;
      if (!previous || !current) {
        continue;
      }

      const legMeters = haversineKm(previous, current) * 1000;
      const approach = legApproach(previous, current, coordinate, legStartKm, legMeters);
      if (approach.meters <= proximityMeters) {
        if (!best || approach.meters < best.meters) {
          best = approach;
        }

        continue;
      }

      // The line left the proximity radius: emit the closest point of the pass that
      // just ended, then reset so a later re-approach is a separate appearance.
      if (best) {
        projections.push({ distanceKm: best.distanceKm, waypointId: waypoint.id });
        best = null;
      }
    }

    // A pass still open at the end of the line emits its closest point too.
    if (best) {
      projections.push({ distanceKm: best.distanceKm, waypointId: waypoint.id });
    }
  }

  return projections;
};
